import { useState, useCallback, useRef } from 'react';
import { addCacheBuster, extractS3KeyFromUrl } from '../utils/imageUtils';

/**
 * Hook for managing image cache-busting across the application
 * This ensures that when images are rotated or updated, all components
 * display the fresh version instead of cached versions
 */
export const useImageCache = () => {
  const [imageReloadKeys, setImageReloadKeys] = useState<{[key: string]: number}>({});
  const lastUpdateRef = useRef<{[key: string]: number}>({});

  /**
   * Gets a cache-busted URL for an image
   * @param url - The original image URL
   * @returns The URL with cache-busting parameters
   */
  const getCacheBustedUrl = useCallback((url: string): string => {
    if (!url || typeof url !== 'string') {
      return url;
    }

    // Extract S3 key from proxy URL for cache key
    const s3Key = extractS3KeyFromUrl(url);
    const cacheKey = s3Key || url;
    
    // Get the reload key for this image
    const reloadKey = imageReloadKeys[cacheKey] || 0;
    
    // Add cache buster with the reload key
    return addCacheBuster(url, reloadKey);
  }, [imageReloadKeys]);

  /**
   * Forces a refresh of a specific image
   * @param imageUrlOrKey - The image URL or S3 key to refresh
   */
  const refreshImage = useCallback((imageUrlOrKey: string) => {
    const s3Key = extractS3KeyFromUrl(imageUrlOrKey);
    const cacheKey = s3Key || imageUrlOrKey;
    
    // Prevent rapid successive updates
    const now = Date.now();
    const lastUpdate = lastUpdateRef.current[cacheKey] || 0;
    if (now - lastUpdate < 100) { // 100ms debounce
      return;
    }
    lastUpdateRef.current[cacheKey] = now;

    setImageReloadKeys(prev => ({
      ...prev,
      [cacheKey]: (prev[cacheKey] || 0) + 1
    }));

    console.log(`Refreshing image cache for: ${cacheKey}`);
  }, []);

  /**
   * Forces a refresh of multiple images
   * @param imageUrlsOrKeys - Array of image URLs or S3 keys to refresh
   */
  const refreshImages = useCallback((imageUrlsOrKeys: string[]) => {
    const updates: {[key: string]: number} = {};
    const now = Date.now();

    imageUrlsOrKeys.forEach(imageUrlOrKey => {
      const s3Key = extractS3KeyFromUrl(imageUrlOrKey);
      const cacheKey = s3Key || imageUrlOrKey;
      
      // Prevent rapid successive updates
      const lastUpdate = lastUpdateRef.current[cacheKey] || 0;
      if (now - lastUpdate < 100) { // 100ms debounce
        return;
      }
      lastUpdateRef.current[cacheKey] = now;

      updates[cacheKey] = (imageReloadKeys[cacheKey] || 0) + 1;
    });

    if (Object.keys(updates).length > 0) {
      setImageReloadKeys(prev => ({
        ...prev,
        ...updates
      }));

      console.log(`Refreshing image cache for ${Object.keys(updates).length} images`);
    }
  }, [imageReloadKeys]);

  /**
   * Clears all image cache keys
   */
  const clearAllCache = useCallback(() => {
    setImageReloadKeys({});
    lastUpdateRef.current = {};
    console.log('Cleared all image cache keys');
  }, []);

  return {
    getCacheBustedUrl,
    refreshImage,
    refreshImages,
    clearAllCache,
    imageReloadKeys
  };
}; 